import { zh } from '../../utils/zh';

export interface RecipientContact {
    id: string;
    campaignId: string;
    campaignName: string;
    region: string;
    name: string | null;
    title: string | null;
    companyName: string | null;
    country: string | null;
    countryCode: string | null;
    language: string | null;
    sourceUrl: string | null;
    customFields: Record<string, unknown>;
    status: string;
}

export interface ReviewedRecipient {
    email: string;
    contacts: RecipientContact[];
    bounced: boolean;
    hardBounced: boolean;
    hardBounceCount: number;
    lastHardBounce: { source: string; statusCode: string | null; diagnostic: string | null; occurredAt: string; senderEmail: string | null } | null;
    hasFailure: boolean;
    failureCount: number;
    failedCount: number;
    uncertainCount: number;
    retryingCount: number;
    suppression: { reason: string; source: string; createdAt: string } | null;
    lastFailure: {
        jobId: string;
        status: string;
        senderEmail: string | null;
        errorCategory: string | null;
        errorCode: string | null;
        errorMessage: string | null;
        attemptNo: number;
        occurredAt: string;
    } | null;
}

export type RecipientFilter = 'all' | 'hard_bounced' | 'failed' | 'excluded';
export interface RecipientPage {
    recipients: ReviewedRecipient[];
    total: number;
    summary: { total: number; hardBounced: number; failed: number; excluded: number };
    limit: number;
    offset: number;
    hasMore: boolean;
}

export function fieldText(value: unknown): string {
    if (value == null) return '';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

// Keep one export row for each list membership. A shared email may have a
// different name or different template variables in each source list.
export function recipientExportRows(recipients: ReviewedRecipient[]): Record<string, string | number>[] {
    return recipients.flatMap((recipient) => recipient.contacts.map((contact) => ({
        邮箱: recipient.email,
        姓名: contact.name || '',
        职位: contact.title || '',
        公司: contact.companyName || '',
        国家: contact.country || '',
        国家代码: contact.countryCode || '',
        语言: contact.language || '',
        所属活动: contact.campaignName,
        活动编号: contact.campaignId,
        地区: zh(contact.region),
        联系人状态: zh(contact.status),
        来源链接: contact.sourceUrl || '',
        已排除: recipient.suppression ? '是' : '否',
        排除原因: recipient.suppression?.reason || '',
        排除来源: recipient.suppression ? zh(recipient.suppression.source) : '',
        硬退信: recipient.hardBounced ? '是' : '否',
        硬退信次数: recipient.hardBounceCount,
        最近退信时间: recipient.lastHardBounce?.occurredAt || '',
        退信错误码: recipient.lastHardBounce?.statusCode || '',
        退信原因: recipient.lastHardBounce?.diagnostic || '',
        退信来源: recipient.lastHardBounce ? zh(recipient.lastHardBounce.source) : '',
        失败批次: recipient.failedCount,
        结果不确定批次: recipient.uncertainCount,
        待重试批次: recipient.retryingCount,
        最近失败状态: zh(recipient.lastFailure?.status || (recipient.bounced ? 'bounced' : '')),
        失败类型: zh(recipient.lastFailure?.errorCategory),
        失败错误码: recipient.lastFailure?.errorCode || '',
        失败原因: recipient.lastFailure?.errorMessage || '',
        最近失败时间: recipient.lastFailure?.occurredAt || '',
        ...Object.fromEntries(Object.entries(contact.customFields || {}).map(([key, value]) => [`变量：${key}`, fieldText(value)])),
    })));
}

export function previewRecipients(campaignId: string): ReviewedRecipient[] {
    return ['amina', 'omar', 'lina'].map((name, index) => ({
        email: `${name}@example.com`,
        contacts: [{
            id: `preview-contact-${index}`, campaignId, campaignName: '示例法律行业联系人', region: 'ae',
            name: ['Amina Hassan', 'Omar Salem', 'Lina Mansour'][index],
            title: ['Partner', 'Legal Counsel', 'Associate'][index],
            companyName: ['Cedar Legal', 'Atlas Advisory', 'Cedar Legal'][index],
            country: 'Saudi Arabia', countryCode: 'SA', language: 'en', sourceUrl: '',
            customFields: { 'Practice Area': ['Corporate', 'Commercial', 'Disputes'][index], City: ['Riyadh', 'Jeddah', 'Riyadh'][index] },
            status: index === 1 ? 'bounced' : 'approved',
        }],
        bounced: index === 1, hasFailure: index < 2, failureCount: index < 2 ? 1 : 0,
        hardBounced: index === 1, hardBounceCount: index === 1 ? 1 : 0,
        lastHardBounce: index === 1 ? { source: 'outlook_dsn', statusCode: '5.1.1', diagnostic: '收件邮箱不存在', occurredAt: '2026-09-07T09:00:00Z', senderEmail: 'sender@example.com' } : null,
        failedCount: index < 2 ? 1 : 0, uncertainCount: 0, retryingCount: 0,
        suppression: index === 1 ? { reason: '收件邮箱不存在', source: 'outlook_dsn', createdAt: '2026-09-07T09:00:00Z' } : null,
        lastFailure: index < 2 ? {
            jobId: 'sample-job', status: 'failed', senderEmail: 'sender@example.com',
            errorCategory: index === 0 ? 'connection' : 'invalid_recipient',
            errorCode: index === 0 ? 'ETIMEDOUT' : '550',
            errorMessage: index === 0 ? '多次重试后连接仍超时，再次发送前请先审核。' : '收件邮箱不存在',
            attemptNo: index === 0 ? 3 : 1, occurredAt: '2026-09-07T09:00:00Z',
        } : null,
    }));
}
